import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

/**
 * Tamanho do texto — Normal, Grande, Maior.
 *
 * "No puedo configurar el tamaño del texto, es muy pequeño... tuve que hacerlo
 * desde el modo de lupa de mi móvil" (avaliação de 26/07). O app inteiro lê
 * tamanho das variáveis --t-* em sistema.css, e elas são em px de propósito —
 * não seguem o ajuste de fonte do celular. Então o ajuste tem que existir
 * dentro do app.
 *
 * Isto é o espelho do ThemeProvider: um valor em localStorage, um atributo na
 * raiz, e sistema.css redefine as variáveis para cada valor. Tudo que está
 * no sistema escala junto; tela que ainda usa `text-sm` solto não escala, e
 * essa é a lista do que falta trazer para o sistema.
 */
export type TamanhoTexto = "normal" | "grande" | "maior";

export const TAMANHOS_TEXTO: readonly TamanhoTexto[] = ["normal", "grande", "maior"];

const STORAGE_KEY = "glucofy-texto";

interface TamanhoTextoContextValue {
  tamanho: TamanhoTexto;
  setTamanho: (t: TamanhoTexto) => void;
}

const TamanhoTextoContext = createContext<TamanhoTextoContextValue>({
  tamanho: "normal",
  setTamanho: () => {},
});

export const lerTamanhoTextoSalvo = (): TamanhoTexto => {
  if (typeof window === "undefined") return "normal";
  try {
    const salvo = window.localStorage.getItem(STORAGE_KEY);
    return (TAMANHOS_TEXTO as readonly string[]).includes(salvo ?? "") ? (salvo as TamanhoTexto) : "normal";
  } catch {
    return "normal";
  }
};

/** Aplica na raiz. Chamado também antes do React montar, para não piscar. */
export const aplicarTamanhoTexto = (t: TamanhoTexto) => {
  if (typeof document === "undefined") return;
  if (t === "normal") delete document.documentElement.dataset.texto;
  else document.documentElement.dataset.texto = t;
};

export const TamanhoTextoProvider = ({ children }: { children: ReactNode }) => {
  const [tamanho, setTamanhoState] = useState<TamanhoTexto>(lerTamanhoTextoSalvo);

  useEffect(() => {
    aplicarTamanhoTexto(tamanho);
    try {
      window.localStorage.setItem(STORAGE_KEY, tamanho);
    } catch {
      /* armazenamento indisponível: fica só na sessão */
    }
  }, [tamanho]);

  const setTamanho = (t: TamanhoTexto) => {
    if ((TAMANHOS_TEXTO as readonly string[]).includes(t)) setTamanhoState(t);
  };

  return (
    <TamanhoTextoContext.Provider value={{ tamanho, setTamanho }}>{children}</TamanhoTextoContext.Provider>
  );
};

export const useTamanhoTexto = () => useContext(TamanhoTextoContext);
